import {
  CalendarProps,
  DatePicker,
  DatePickerChangeEvent,
  DateTimePicker,
  DateTimePickerChangeEvent,
  MultiViewCalendarProps
} from '@progress/kendo-react-dateinputs';
import { RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import React, { RefObject } from 'react';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { UnifiedCalendar } from './dateTimeRange/UnifiedDateCalendar';
import { CustomDatePopup } from './CustomDatePopup';
import { CustomDateTimeInput } from './CustomDateTimeInput';
import { GlobalFilterState } from './GlobalFilterState';
import { CPS_FIELD_TYPE } from '@cs-cms/types';
import { PICKER_END_ID, PICKER_START_ID } from './groupedDateGlobalFilterUtils';

const DUMMY_VALUE = 'x';

interface GroupedDatePickerRangeProps {
  dateFiltersState: GlobalFilterState[];
  fieldSelected: GlobalFilterState;
  fieldDisplayName: string;
  translate: (key: string) => string;
  currentValueStart: Date | null;
  currentValueEnd: Date | null | undefined;
  todayEndOfTheDay: Date;
  startPickerOpen: boolean;
  endPickerOpen: boolean;
  handleFieldChange: (event: RadioButtonChangeEvent) => void;
  handleChange: (event: DateTimePickerChangeEvent | DatePickerChangeEvent) => void;
  handlePopupOpen: (pickerId: string) => void;
  handlePopupClose: (pickerId: string) => void;
  dateTimeStartRef: RefObject<DateTimePicker | null>;
  dateTimeEndRef: RefObject<DateTimePicker | null>;
  dateStartRef: RefObject<DatePicker | null>;
  dateEndRef: RefObject<DatePicker | null>;
}

export const GroupedDatePickerRange: React.FC<GroupedDatePickerRangeProps> = props => {
  const {
    dateFiltersState,
    fieldSelected,
    fieldDisplayName,
    translate,
    currentValueStart,
    currentValueEnd,
    todayEndOfTheDay,
    startPickerOpen,
    endPickerOpen,
    handleFieldChange,
    handleChange,
    handlePopupOpen,
    handlePopupClose,
    dateTimeStartRef,
    dateTimeEndRef,
    dateStartRef,
    dateEndRef
  } = props;

  const startLabel = `${fieldDisplayName} (${translate('daterangepicker.start')})`;
  const endLabel = `${fieldDisplayName} (${translate('daterangepicker.end')})`;
  const hasEndValue = Boolean(currentValueEnd);

  const renderUnifiedCalendar = (
    calendarProps: CalendarProps | MultiViewCalendarProps
  ): React.ReactElement => (
    <UnifiedCalendar
      calendarProps={calendarProps}
      dateFiltersState={dateFiltersState}
      fieldSelected={fieldSelected}
      handleFieldChange={handleFieldChange}
    />
  );

  const renderDateTimeCalendar = (calendarProps: CalendarProps): React.ReactElement =>
    renderUnifiedCalendar(calendarProps);

  const renderDateCalendar = (calendarProps: MultiViewCalendarProps): React.ReactElement =>
    renderUnifiedCalendar(calendarProps);

  if (fieldSelected.type === CPS_FIELD_TYPE.DATETIME) {
    return (
      <>
        <FloatingLabel editorId={PICKER_START_ID} editorValue={DUMMY_VALUE} label={startLabel}>
          <DateTimePicker
            className="volvoDatePicker"
            ref={dateTimeStartRef}
            id={PICKER_START_ID}
            value={currentValueStart}
            popup={CustomDatePopup}
            show={startPickerOpen}
            onOpen={() => handlePopupOpen(PICKER_START_ID)}
            onClose={() => handlePopupClose(PICKER_START_ID)}
            calendar={renderDateTimeCalendar}
            onChange={handleChange}
          />
        </FloatingLabel>
        <FloatingLabel editorId={PICKER_END_ID} editorValue={DUMMY_VALUE} label={endLabel}>
          <DateTimePicker
            className="volvoDatePicker"
            key={hasEndValue ? 'endDefined' : 'endUndefined'}
            ref={dateTimeEndRef}
            id={PICKER_END_ID}
            value={hasEndValue ? currentValueEnd : undefined}
            valid={true}
            popup={CustomDatePopup}
            show={endPickerOpen}
            onOpen={() => handlePopupOpen(PICKER_END_ID)}
            onClose={() => handlePopupClose(PICKER_END_ID)}
            defaultValue={todayEndOfTheDay}
            dateInput={inputProps => (
              <CustomDateTimeInput
                value={inputProps.value}
                onChange={inputProps.onChange}
                isDefaultValue={!hasEndValue}
              />
            )}
            calendar={renderDateTimeCalendar}
            onChange={handleChange}
          />
        </FloatingLabel>
      </>
    );
  }

  return (
    <>
      <FloatingLabel editorId={PICKER_START_ID} editorValue={DUMMY_VALUE} label={startLabel}>
        <DatePicker
          className="volvoDatePicker"
          ref={dateStartRef}
          id={PICKER_START_ID}
          value={currentValueStart}
          show={startPickerOpen}
          popupSettings={{ animate: false }}
          onOpen={() => handlePopupOpen(PICKER_START_ID)}
          onClose={() => handlePopupClose(PICKER_START_ID)}
          calendar={renderDateCalendar}
          onChange={handleChange}
        />
      </FloatingLabel>
      <FloatingLabel editorId={PICKER_END_ID} editorValue={DUMMY_VALUE} label={endLabel}>
        <DatePicker
          className="volvoDatePicker"
          ref={dateEndRef}
          id={PICKER_END_ID}
          value={currentValueEnd ?? null}
          show={endPickerOpen}
          popupSettings={{ animate: false }}
          onOpen={() => handlePopupOpen(PICKER_END_ID)}
          onClose={() => handlePopupClose(PICKER_END_ID)}
          calendar={renderDateCalendar}
          onChange={handleChange}
        />
      </FloatingLabel>
    </>
  );
};
